import React, { useEffect, useRef, useState } from 'react';
import { Container, Box, Typography, Button, Alert } from '@mui/material';
import { useNavigate } from 'react-router-dom';

// Mini helper pentru feedback audio (beep)
const beep = (duration = 180, frequency = 1100, volume = 0.15) => {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    gain.gain.value = volume;
    osc.frequency.value = frequency;
    osc.type = 'square';
    osc.start();
    setTimeout(() => { osc.stop(); ctx.close(); }, duration);
  } catch (_) { /* ignore */ }
};

// ZXing reader ref (for fallback when BarcodeDetector is unavailable)
const zxingReaderRef = { current: null };

const hasBarcodeDetector = () => 'BarcodeDetector' in window;

export default function MobileScan() {
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [error, setError] = useState('');
  const [scanning, setScanning] = useState(false);
  const [overlayColor, setOverlayColor] = useState('yellow');
  const [flash, setFlash] = useState(false);
  const [zxingSupported, setZxingSupported] = useState(false);

  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(t => t.stop());
      }
    };
  }, []);

  const startScan = async () => {
    setError('');

    // Try native BarcodeDetector first
    const supportsNative = hasBarcodeDetector();

    if (supportsNative) {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' }, audio: false });
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play();
        }
        setScanning(true);
        const detector = new window.BarcodeDetector({ formats: ['qr_code'] });

        const tick = async () => {
          if (!videoRef.current || !scanning) return;
          try {
            const bitmap = await createImageBitmap(videoRef.current);
            const codes = await detector.detect(bitmap);
            if (codes && codes.length > 0) {
              const raw = (codes[0].rawValue || '').trim();
              handleCode(raw);
              return; // stop on first hit
            }
          } catch (_) { /* ignore transient */ }
          requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
        return;
      } catch (e) {
        // fall through to ZXing
        console.warn('Native BarcodeDetector failed, trying ZXing fallback...', e);
      }
    }

    // ZXing fallback
    try {
      const ZXing = await import(/* webpackChunkName: "zxing" */ '@zxing/browser');
      setZxingSupported(true);

      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' }, audio: false });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      setScanning(true);

      const reader = new ZXing.BrowserMultiFormatReader();
      zxingReaderRef.current = reader;

      await reader.decodeFromVideoDevice(null, videoRef.current, (result, err) => {
        if (result) {
          const text = (result.getText && result.getText()) || '';
          if (text) {
            handleCode(text.trim());
          }
        }
        // errors are frequent during scanning; ignore transient ones
      });
    } catch (e) {
      console.error('ZXing fallback not available:', e);
      setError('Browserul nu suportă BarcodeDetector, iar fallback-ul ZXing nu este disponibil. Rulează: `npm i @zxing/browser` și repornește.');
      return;
    }
  };

  const stopScan = () => {
    setScanning(false);
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    try {
      if (zxingReaderRef.current && zxingReaderRef.current.reset) {
        zxingReaderRef.current.reset();
        zxingReaderRef.current = null;
      }
    } catch (_) {}
    setOverlayColor('yellow');
  };

  const handleCode = (text) => {
    stopScan();
    try {
      // Folosim exact textul din QR. Dacă este URL absolut (https://...), îl deschidem ca atare.
      const raw = (text || '').trim();

      // feedback înainte de navigare
      setOverlayColor('lime');
      beep();
      try {
        if (navigator.vibrate) navigator.vibrate(80);
        if (window?.webkit?.messageHandlers?.haptic) {
          window.webkit.messageHandlers.haptic.postMessage('impactMedium');
        }
      } catch (_) {}
      setFlash(true);
      setTimeout(() => setFlash(false), 120);

      let isAbsolute = false;
      try {
        // dacă parsează, e URL absolut
        // (Safari pe iOS parsează https:// corect)
        // dacă arunca excepție, tratăm ca path intern
        // eslint-disable-next-line no-new
        new URL(raw);
        isAbsolute = true;
      } catch (_) {}

      setTimeout(() => {
        if (isAbsolute) {
          // deschidem direct URL-ul primit din backend
          window.location.assign(raw);
        } else {
          // fallback: path intern /mobile-product/:id
          const match = raw.match(/\/mobile-product\/(\d+)/);
          if (match) {
            const id = match[1];
            navigate(`/mobile-prodwdeqfrg il6;uct/${id}`, { replace: true });
          } else {
            setOverlayColor('orange');
            setTimeout(() => setOverlayColor('yellow'), 400);
            setError('QR recunoscut, dar nu conține un link valid.');
          }
        }
      }, 180);
    } catch (e) {
      setError('Eroare la procesarea codului: ' + (e.message || e.toString()));
    }
  };

  return (
    <Container disableGutters sx={{ p: 2 }}>
      <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>Scanează eticheta produsului</Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {!hasBarcodeDetector() && (
        <Alert severity={zxingSupported ? 'info' : 'warning'} sx={{ mb: 2 }}>
          {zxingSupported ? (
            'Acest dispozitiv nu are BarcodeDetector, dar am pornit automat fallback-ul ZXing. Apasă „Scanează”, apoi permite accesul la cameră.'
          ) : (
            'Safari/iPhone nu are BarcodeDetector — e normal. Apasă „Scanează” și permite camera; dacă nu pornește, asigură-te că pachetul @zxing/browser este instalat în proiect (npm i @zxing/browser) și repornește aplicația.'
          )}
        </Alert>
      )}

      <Box sx={{ position: 'relative', width: '100%', aspectRatio: '3 / 4', background: '#000', borderRadius: 2, overflow: 'hidden', mb: 2 }}>
        <video ref={videoRef} playsInline muted style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        {/* Overlay ghidaj pentru încadrara QR */}
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '70%',
            aspectRatio: '1 / 1',
            borderRadius: 2,
            border: `4px solid ${overlayColor}`,
            boxShadow: 3,
            pointerEvents: 'none',
          }}
        />
        {/* Colțuri vizuale */}
        <Box sx={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
          <Box sx={{ position: 'absolute', top: '15%', left: '15%', width: 24, height: 24, borderTop: `4px solid ${overlayColor}`, borderLeft: `4px solid ${overlayColor}` }} />
          <Box sx={{ position: 'absolute', top: '15%', right: '15%', width: 24, height: 24, borderTop: `4px solid ${overlayColor}`, borderRight: `4px solid ${overlayColor}` }} />
          <Box sx={{ position: 'absolute', bottom: '15%', left: '15%', width: 24, height: 24, borderBottom: `4px solid ${overlayColor}`, borderLeft: `4px solid ${overlayColor}` }} />
          <Box sx={{ position: 'absolute', bottom: '15%', right: '15%', width: 24, height: 24, borderBottom: `4px solid ${overlayColor}`, borderRight: `4px solid ${overlayColor}` }} />
        </Box>
        {flash && (
          <Box sx={{ position: 'absolute', inset: 0, backgroundColor: '#fff', opacity: 0.6, pointerEvents: 'none' }} />
        )}
        {!scanning && (
          <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography variant="body2" color="#fff" sx={{ opacity: 0.8 }}>
              Apasă „Scanează” pentru a porni camera
            </Typography>
          </Box>
        )}
      </Box>

      <Box display="flex" gap={1}>
        {!scanning ? (
          <Button variant="contained" onClick={startScan}>Scanează</Button>
        ) : (
          <Button variant="outlined" onClick={stopScan}>Oprește</Button>
        )}
        <Button variant="text" onClick={() => navigate(-1)}>Înapoi</Button>
      </Box>
    </Container>
  );
}